import { useState } from 'react';

import { useForm } from 'react-hook-form';

import { SaveButton } from '../../components/Clickables/Buttons/SaveButton';
import { ExternalLink } from '../../components/Clickables/ExternalLink/ExternalLink';
import { InternalLink } from '../../components/Clickables/InternalLink/InternalLink';
import { Container } from '../../components/Container/Container';
import { ErrorBanner } from '../../components/ErrorBanner/ErrorBanner';
import { Heading } from '../../components/Heading/Heading';
import { HooksFormInput } from '../../components/Inputs/FormInput/HooksFormInput';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { Redirect } from '../../components/Redirect/Redirect';
import { Text } from '../../components/Text/Text';
import { DEFAULT_LOGGED_IN_ROUTE } from '../../constants/constants';
import { reportMykissClientError } from '../../helpers/errorHandling/client';
import { ErrorMessage } from '../../helpers/forms';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';

interface PasswordResetState {
  email: string;
}

interface IProps {
  resetPassword: (email: string) => Promise<unknown>;
  appView: boolean;
}

export const PasswordResetInner = ({ resetPassword, appView }: IProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { isLoggedIn } = useUserInfo();

  const [submitErrors, setSubmitErrors] = useState<ErrorMessage[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetState>();

  if (isLoggedIn) {
    return <Redirect to={DEFAULT_LOGGED_IN_ROUTE} />;
  }

  const onSubmit = async ({ email }: PasswordResetState): Promise<void> => {
    setIsLoading(true);
    try {
      await resetPassword(email);
      setEmailSent(true);
    } catch (error) {
      const safeError: any = error;
      if (
        safeError.name === 'InvalidParameterException' &&
        safeError.message.startsWith(
          'Cannot reset password for the user as there is no registered/verified email',
        )
      ) {
        setSubmitErrors([
          {
            errorMessage: (
              <>
                The account you entered either doesn&apos;t exist or wasn&apos;t verified.{' '}
                <ExternalLink
                  href="https://fishbrain.helpshift.com/a/fishbrain---social-fishing-forecast-app/"
                  variant="textNone"
                  className="underline"
                >
                  Please contact our Support team
                </ExternalLink>{' '}
                for further help.
              </>
            ),
          },
        ]);
      } else if (safeError.name === 'LimitExceededException') {
        setSubmitErrors([safeError.message]);
      } else if (safeError.name === 'UserNotFoundException') {
        setSubmitErrors([
          {
            errorMessage: (
              <>
                We could not reset the password of this account.{' '}
                {!appView && (
                  <>
                    If the account was created using Google/Facebook/Apple sign-in,{' '}
                    <InternalLink to="/login" variant="textNone" className="underline">
                      please login here
                    </InternalLink>
                    . If not,{' '}
                  </>
                )}
                <ExternalLink
                  href="https://fishbrain.helpshift.com/a/fishbrain---social-fishing-forecast-app/"
                  variant="textNone"
                  className="underline"
                >
                  please contact our Support team
                </ExternalLink>{' '}
                for further help.
              </>
            ),
          },
        ]);
      } else {
        reportMykissClientError(safeError);
        setSubmitErrors([
          {
            errorMessage: (
              <>
                Something went wrong while trying to reset your password.{' '}
                <ExternalLink
                  href="https://fishbrain.helpshift.com/a/fishbrain---social-fishing-forecast-app/"
                  variant="textNone"
                  className="underline"
                >
                  Please contact our Support team
                </ExternalLink>{' '}
                for further help.
              </>
            ),
          },
        ]);
      }
      setIsLoading(false);
    }
  };

  const metaTitle = 'Reset your password at Fishbrain';
  const metaCanonical = '/password_reset';

  if (emailSent) {
    return (
      <Container variant="extraThin" className="mt-16 mb-32">
        <MetaTags
          title={metaTitle}
          description="An email has been sent to your email address with further instructions on how to reset
          your password."
          link={metaCanonical}
        />
        <Heading as="h1" className="mb-4 text-center">
          Password Reset
        </Heading>
        <Text className="text-center">
          An email has been sent to your email address with further instructions on how to reset
          your password.
        </Text>
      </Container>
    );
  }

  return (
    <Container variant="extraThin" className="mt-16 mb-32">
      <MetaTags
        title={metaTitle}
        description="Forgot your password? No worries! Enter your email address and we\'ll send you a code to reset your password."
        link={metaCanonical}
      />
      <Heading as="h1" className="mb-4 text-center">
        Password Reset
      </Heading>
      <Text className="text-center">
        Enter the email address you signed up with and we&apos;ll send a code to confirm the
        address.
      </Text>
      <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
        <HooksFormInput<PasswordResetState>
          id="email"
          label="Email"
          inputProps={{ type: 'email' }}
          error={errors.email}
          className="mb-4 font-semibold"
          register={register}
          registerOptions={{ required: 'No email was entered.' }}
        />
        <ErrorBanner errors={submitErrors} />
        <SaveButton
          isSaving={isLoading}
          type="submit"
          className="mt-auto mb-0 ml-auto w-min"
          savingLabel="Sending..."
        >
          Send
        </SaveButton>
      </form>
    </Container>
  );
};
